<template>
	<div v-if="isVisible" class="modal-overlay">
		<div class="modal-content">
			<!-- Шаг 1: Ввод email -->
			<div v-if="step === 1" class="step-container">
				<h2 class="modal-title">Восстановление пароля</h2>
				<p class="modal-description">
					Введите email, указанный при регистрации. Мы отправим код для восстановления пароля.
				</p>

				<div class="form-group">
					<label for="reset-email">Email</label>
					<input
						id="reset-email"
						v-model="email"
						type="email"
						placeholder="example@mail.com"
						class="form-input"
						:class="{ error: emailError }"
						@input="emailError = ''"
						@keyup.enter="sendCode"
					/>
					<span v-if="emailError" class="error-message">{{ emailError }}</span>
				</div>

				<div class="modal-actions">
					<button type="button" class="btn btn-secondary" @click="close">
						Отмена
					</button>
					<button
						type="button"
						class="btn btn-primary"
						:disabled="isLoading"
						@click="sendCode"
					>
						{{ isLoading ? 'Отправка...' : 'Отправить код' }}
					</button>
				</div>
			</div>

			<!-- Шаг 2: Ввод кода -->
			<div v-if="step === 2" class="step-container">
				<h2 class="modal-title">Подтверждение email</h2>
				<p class="modal-description">
					Код отправлен на <strong>{{ email }}</strong>
				</p>

				<div class="form-group">
					<label for="reset-code">Код подтверждения</label>
					<input
						id="reset-code"
						v-model="code"
						type="text"
						placeholder="000000"
						maxlength="6"
						class="form-input code-input"
						:class="{ error: codeError }"
						@input="handleCodeInput"
						@keyup.enter="verifyCode"
					/>
					<span v-if="codeError" class="error-message">{{ codeError }}</span>
				</div>

				<p class="resend-text">
					Не получили код?
					<button
						type="button"
						class="link-button"
						:disabled="resendTimer > 0"
						@click="sendCode"
					>
						{{ resendTimer > 0 ? `Отправить повторно (${resendTimer}с)` : 'Отправить повторно' }}
					</button>
				</p>

				<div class="modal-actions">
					<button type="button" class="btn btn-secondary" @click="goBack">
						Назад
					</button>
					<button
						type="button"
						class="btn btn-primary"
						:disabled="isLoading || code.length !== 6"
						@click="verifyCode"
					>
						{{ isLoading ? 'Проверка...' : 'Подтвердить' }}
					</button>
				</div>
			</div>

			<!-- Шаг 3: Новый пароль -->
			<div v-if="step === 3" class="step-container">
				<h2 class="modal-title">Новый пароль</h2>
				<p class="modal-description">Придумайте надежный пароль</p>

				<div class="form-group">
					<label for="new-password">Новый пароль</label>
					<input
						id="new-password"
						v-model="newPassword"
						type="password"
						placeholder="Минимум 8 символов"
						class="form-input"
						:class="{ error: passwordError }"
						@input="passwordError = ''"
					/>
					<span v-if="passwordError" class="error-message">{{ passwordError }}</span>
				</div>

				<div class="form-group">
					<label for="confirm-password">Подтвердите пароль</label>
					<input
						id="confirm-password"
						v-model="confirmPassword"
						type="password"
						placeholder="Повторите пароль"
						class="form-input"
						:class="{ error: confirmError }"
						@input="confirmError = ''"
						@keyup.enter="resetPassword"
					/>
					<span v-if="confirmError" class="error-message">{{ confirmError }}</span>
				</div>

				<div class="modal-actions">
					<button type="button" class="btn btn-secondary" @click="close">
						Отмена
					</button>
					<button
						type="button"
						class="btn btn-primary"
						:disabled="isLoading"
						@click="resetPassword"
					>
						{{ isLoading ? 'Сохранение...' : 'Сохранить пароль' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { useAppStore } from '@/stores/useAppStore'
import authService from '@/services/auth'

export default {
	name: 'ForgotPasswordModal',

	props: {
		visible: {
			type: Boolean,
			default: false,
		},
	},

	emits: ['close', 'success'],

	data() {
		return {
			step: 1, // 1 - email, 2 - код, 3 - новый пароль
			email: '',
			code: '',
			newPassword: '',
			confirmPassword: '',
			emailError: '',
			codeError: '',
			passwordError: '',
			confirmError: '',
			isLoading: false,
			resendTimer: 0,
			resendInterval: null,
		}
	},

	computed: {
		isVisible() {
			return this.visible
		},

		appStore() {
			return useAppStore()
		},
	},

	watch: {
		visible(newVal) {
			if (newVal) {
				this.resetForm()
			}
		},
	},

	beforeUnmount() {
		this.clearResendTimer()
	},

	methods: {
		close() {
			this.$emit('close')
		},

		resetForm() {
			this.step = 1
			this.email = ''
			this.code = ''
			this.newPassword = ''
			this.confirmPassword = ''
			this.emailError = ''
			this.codeError = ''
			this.passwordError = ''
			this.confirmError = ''
			this.clearResendTimer()
		},

		goBack() {
			if (this.step > 1) {
				this.step--
				this.code = ''
				this.codeError = ''
			}
		},

		handleCodeInput(e) {
			// Только цифры
			this.code = e.target.value.replace(/\D/g, '')
			this.codeError = ''
		},

		startResendTimer() {
			this.resendTimer = 60
			this.resendInterval = setInterval(() => {
				this.resendTimer--
				if (this.resendTimer <= 0) {
					this.clearResendTimer()
				}
			}, 1000)
		},

		clearResendTimer() {
			if (this.resendInterval) {
				clearInterval(this.resendInterval)
				this.resendInterval = null
			}
			this.resendTimer = 0
		},

		validateEmail() {
			if (!this.email) {
				this.emailError = 'Введите email'
				return false
			}
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(this.email)) {
				this.emailError = 'Некорректный email'
				return false
			}
			return true
		},

		validatePassword() {
			if (!this.newPassword) {
				this.passwordError = 'Введите пароль'
				return false
			}
			if (this.newPassword.length < 8) {
				this.passwordError = 'Пароль должен содержать минимум 8 символов'
				return false
			}
			if (!this.confirmPassword) {
				this.confirmError = 'Подтвердите пароль'
				return false
			}
			if (this.newPassword !== this.confirmPassword) {
				this.confirmError = 'Пароли не совпадают'
				return false
			}
			return true
		},

		async sendCode() {
			if (!this.validateEmail()) return

			this.isLoading = true

			try {
				await authService.forgotPassword(this.email)
				this.appStore.showSuccess('Код отправлен на email')
				this.step = 2
				this.startResendTimer()
			} catch (error) {
				this.emailError = error.message || 'Ошибка отправки кода'
			} finally {
				this.isLoading = false
			}
		},

		async verifyCode() {
			if (this.code.length !== 6) {
				this.codeError = 'Введите 6-значный код'
				return
			}

			this.isLoading = true

			try {
				await authService.verifyResetCode(this.email, this.code)
				this.step = 3
			} catch (error) {
				this.codeError = error.message || 'Неверный код'
			} finally {
				this.isLoading = false
			}
		},

		async resetPassword() {
			if (!this.validatePassword()) return

			this.isLoading = true

			try {
				await authService.resetPassword(this.email, this.code, this.newPassword)
				this.appStore.showSuccess('Пароль успешно изменен')
				this.$emit('success')
				this.close()
			} catch (error) {
				this.passwordError = error.message || 'Ошибка сброса пароля'
			} finally {
				this.isLoading = false
			}
		},
	},
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
	padding: 20px;
}

.modal-content {
	background: white;
	border-radius: 12px;
	padding: 40px;
	max-width: 480px;
	width: 100%;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.step-container {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.modal-title {
	font-size: 24px;
	font-weight: 600;
	color: #333;
	margin: 0;
}

.modal-description {
	font-size: 14px;
	color: #666;
	margin: 0;
	line-height: 1.5;
}

.form-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.form-group label {
	font-size: 14px;
	font-weight: 500;
	color: #333;
}

.form-input {
	padding: 12px 16px;
	border: 1px solid #ddd;
	border-radius: 8px;
	font-size: 14px;
	transition: all 0.2s;
}

.form-input:focus {
	outline: none;
	border-color: #667eea;
	box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input.error {
	border-color: #ff6b6b;
}

.code-input {
	text-align: center;
	font-size: 24px;
	letter-spacing: 8px;
	font-family: 'Courier New', monospace;
}

.error-message {
	font-size: 12px;
	color: #ff6b6b;
}

.resend-text {
	font-size: 14px;
	color: #666;
	text-align: center;
	margin: 0;
}

.link-button {
	background: none;
	border: none;
	color: #667eea;
	cursor: pointer;
	font-size: 14px;
	text-decoration: underline;
	padding: 0;
	margin-left: 4px;
}

.link-button:hover:not(:disabled) {
	color: #5568d3;
}

.link-button:disabled {
	color: #999;
	cursor: not-allowed;
	text-decoration: none;
}

.modal-actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
}

.btn {
	padding: 12px 24px;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;
	border: none;
}

.btn-primary {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	color: white;
}

.btn-primary:hover:not(:disabled) {
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.btn-secondary {
	background: #f5f5f5;
	color: #666;
}

.btn-secondary:hover {
	background: #e5e5e5;
}
</style>
